import PropTypes from "prop-types";
import styled from "styled-components";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip,
} from "recharts";

import { convertTimestampToDate } from "utils/date";
import { ChartTooltip } from "components/Chart";
import { GradientLineChartPlaceholder } from "./GradientLineChartPlaceholder";

const GradientLineChartContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;

  .no-data {
    color: ${({ theme }) => theme.card.font.secondary};
    font-weight: 500;
    font-size: 14px;
  }
`;

export const GradientLineChart = ({
  data,
  height = "350px",
  title = "Historical Allocation",
  assetsColors = [],
  gridVertical = false,
  fillOpacity = 0.1,
  showDot = false,
  timestamp = "",
  percentageVal = true,
  showXAxis = true,
  showYAxis = true,
  showGridLines = true,
  gradientFillColor = true,
  yAxisProps,
  areaProps,
  isLoading = false,
  noDataText = "No data to display",
}) => {
  const convertValue = (value) => {
    return percentageVal ? `${100 * value}%` : value;
  };

  const convertDate = (value) => {
    return convertTimestampToDate(value, "", timestamp);
  };

  if (isLoading) {
    return <GradientLineChartPlaceholder height={height} />;
  }

  return (
    <GradientLineChartContainer style={{ height }}>
      {title && (
        <h4 className="text-center line-chart-heading mb-5">{title}</h4>
      )}
      {data?.length ? (
        <ResponsiveContainer>
          <AreaChart
            width={200}
            height={240}
            data={data}
            className="chart-wrapper"
          >
            <defs>
              {assetsColors.map((data) => (
                <linearGradient
                  id={data.id}
                  gradientTransform="rotate(100)"
                  key={data.key}
                >
                  <stop offset="0%" stopOpacity={0.47} stopColor={data.color} />
                  <stop
                    offset="100%"
                    stopOpacity={0.12}
                    stopColor={data.color}
                  />
                </linearGradient>
              ))}
            </defs>
            {showXAxis && (
              <XAxis
                domain={[0, "dataMax"]}
                dataKey="name"
                stroke="#ffffff"
                fontSize={10}
                tickFormatter={convertDate}
              />
            )}
            {showYAxis && (
              <YAxis
                stroke="#ffffff"
                fontSize={14}
                tickFormatter={convertValue}
                {...yAxisProps}
              />
            )}
            {showGridLines && (
              <>
                {gridVertical ? (
                  <CartesianGrid stroke="grey" />
                ) : (
                  <CartesianGrid vertical={false} opacity={0.5} />
                )}
              </>
            )}

            <Tooltip content={ChartTooltip} />

            {assetsColors.map((data, inx) => (
              <Area
                key={`data-${inx}-area`}
                type="monotone"
                dataKey={data.key}
                stroke={data.color}
                fillOpacity={fillOpacity}
                fill={gradientFillColor ? `url(#${data.id})` : data.color}
                strokeWidth={3}
                dot={showDot ? { r: 5, strokeWidth: 2 } : false}
                {...areaProps}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <h4 className="no-data">{noDataText}</h4>
      )}
    </GradientLineChartContainer>
  );
};

GradientLineChart.propTypes = {
  data: PropTypes.array,
  height: PropTypes.string,
  title: PropTypes.string,
  assetsColors: PropTypes.array,
  gridVertical: PropTypes.bool,
  fillOpacity: PropTypes.number,
  showDot: PropTypes.bool,
  timestamp: PropTypes.string,
  percentageVal: PropTypes.bool,
  showXAxis: PropTypes.bool,
  showYAxis: PropTypes.bool,
  showGridLines: PropTypes.bool,
  gradientFillColor: PropTypes.bool,
  yAxisProps: PropTypes.object,
  areaProps: PropTypes.object,
  isLoading: PropTypes.bool,
  noDataText: PropTypes.string,
};
